<template>
  <div class="bb-funds">
    <h2 class="bb-page-title">Funds</h2>
    <transition name="fade" appear>
      <loader-fly v-if="loaderRun"/>
    </transition>
    <div class="bb-funds-header">
      <div class="bb-funds-header-info">
        <div class="bb-funds-header-info__wrapper">
          <span class="bb-funds-header-info__title">Date</span>
          <div class="bb-funds-header-info__item">
            <i class="icon-calendar bb-funds-header-info__icon bb-funds-header-info__icon_calendar"/>
            <span>{{today}}</span>
          </div>
        </div>
        <div class="bb-funds-header-info__wrapper">
          <span class="bb-funds-header-info__title">Open Balance</span>
          <div class="bb-funds-header-info__item" @click.stop="setOpenBalance()"
               :class="{'bb-funds-header-info__total': canYouSetTotal === 'youCanSet'}">
            <i class="icon-coins bb-funds-header-info__icon bb-funds-header-info__icon_coin"/>
            <span>
             <ICountUp
               :delay="0"
               :endVal="totalOpenBalance ? totalOpenBalance : 0"
               :options="countUp.options"
             />
          </span>
          </div>
        </div>
        <div class="bb-funds-header-info__wrapper"
             v-for="(total, index) in totals"
             :key="index + total.title">
          <transition name="fade">
            <span class="bb-funds-header-info__title" v-if="totalReady">{{total.title}}</span>
          </transition>
          <div class="bb-funds-header-info__item"
               @click.stop="getCurrentProjects(total)"
               :class="{'bb-funds-header-info__item_active': total.hover}">
            <i class="icon-coins bb-funds-header-info__icon bb-funds-header-info__icon_coin"/>
            <span>
             <ICountUp
               :delay="0"
               :endVal="total.value ? total.value : 0"
               :options="countUp.options"
             />
          </span>
          </div>
        </div>
      </div>
      <div class="bb-funds-header-buttons">
        <button class="bb-funds-header-buttons__item bb-button" @click.stop="openTransferList = true">
          STATEMENT
        </button>
      </div>
    </div>
    <div>
      <Table :options="options" :totalPages="fullFundsInfo.totalPages"/>
      <button class="bb-button-dashed" @click.stop="openCreateFund()">
        <i class="icon-cross"></i>
        Add new
      </button>
      <Pagination :data="fullFundsInfo" :getQuery="getFunds" :limit="defLimit" class="bb-pagination"/>
    </div>
    <transition name="fade" mode="out-in">
      <ModalWindow v-if="openModal || openTransferList" @close="hideModal">
        <div class="bb-funds-modal">
          <template v-if="openTransferList">
            <TransfersList/>
          </template>
          <template v-else-if="openModalBalance">
            <h3 class="bb-funds-modal__title">Opening Balance</h3>
            <input class="bb-input bb-input_border bb-funds-modal__input"
                   v-model="startOpenBalance"
                   @keyup.enter="openBalance()"/>
            <button class="bb-button bb-funds-modal__btn" @click="openBalance()">SAVE</button>
          </template>
          <template v-else>
            <h3 class="bb-funds-modal__title">{{modalTitle}}</h3>
            <TransferWindow v-if="transferFund" @setTransfer="saveModal" :from-fund="transferFund">
              <button class="bb-button bb-funds-modal__button">save</button>
            </TransferWindow>
            <SettingsWindow v-else-if="editableFund || newFund" @setSettings="saveModal" :edit-fund="editableFund">
              <button class="bb-button bb-funds-modal__button">save</button>
            </SettingsWindow>
          </template>
        </div>
      </ModalWindow>
    </transition>
    <transition name="fade" mode="out-in">
      <ModalWindow @close="openEarned = false" v-if="openEarned" :tableMode="true" title="Projects">
       <div class="bb-funds-modal">
         <BBTable :columns="optionsEarnedTable"
                  :rows="kpiEarnedRowsData"
         />
       </div>
      </ModalWindow>
    </transition>
    <transition name="fade" mode="out-in">
      <ModalWindow @close="closeProforma" v-if="proformaOpen">
        <ProformaInvoice @close="proformaOpen = false"
                         :view-mode="true"
                         :editable-invoice="editableInvoice"/>
      </ModalWindow>
    </transition>
  </div>
</template>

<script>
import TransferWindow from '@/components/Finance/Funds/TransferWindow/TransferWindow'
import SettingsWindow from '@/components/Finance/Funds/SettingsWindow/SettingsWindow'
import {fundsTable, fundsTableColumns} from '@/constants/TablesOptions/fundsTable'
import ModalWindow from '@/components/Main/ModalWindow/ModalWindow'
import Table from '@/components/Main/Table/Table'
import convertNumber from '@/mixin/convertNumber'
import ICountUp from 'vue-countup-v2'
import FundsTransferApi from '@/api/FundsTransfer/api'
import InvoicesApi from '@/api/Invoce/api'
import FundsApi from '@/api/Funds/api'
import KpiApi from '@/api/Kpi/api'
import moment from 'moment'
import _ from 'lodash'
import BBTable from '@/components/Main/BBTable/BBTable'
import TransfersList from '@/components/Finance/Funds/TransfersList/TransfersList'
import ProformaInvoice from '@/components/Dissemination/Sales/CRM/ContactCard/ProformaInvoice/ProformaInvoice'
import validateNum from '@/mixin/validateNum'
import Pagination from '@/components/Main/Pagination/Pagination'
import LoaderFly from '@/components/Main/Loaders/LoaderFly'
import setKpiServices from '@/mixin/setKpiServices'
import fundsKPITable from '@/constants/TablesOptions/fundsKPITable'
import kpiEarnedTableColumns from '@/constants/TablesOptions/kpiEarnedTable'

export default {
  name: 'Funds',
  components: {
    Table,
    ICountUp,
    ModalWindow,
    TransferWindow,
    SettingsWindow,
    BBTable,
    ProformaInvoice,
    TransfersList,
    Pagination,
    LoaderFly
  },
  mixins: [convertNumber, validateNum, setKpiServices],
  data () {
    return {
      openEarned: false,
      optionsEarnedTable: fundsKPITable,
      loaderRun: false,
      totalReady: false,
      fullFundsInfo: '',
      defLimit: 30,
      totals: {
        currentMonth: {
          value: 0,
          title: 'Current Month',
          hover: true
        },
        paid: {
          value: 0,
          title: 'Paid'
        },
        remain: {
          value: 0,
          title: 'Remain'
        }
      },
      options: fundsTable,
      canYouSetTotal: false,
      startOpenBalance: 0,
      openModalBalance: false,
      openTransferList: false,
      editableInvoice: {
        invoice: {}
      },
      proformaOpen: false,
      totalToday: false,
      openModal: false,
      newFund: false,
      editableFund: false,
      transferFund: false,
      modalTitle: '',
      countUp: {
        options: {
          useEasing: false,
          useGrouping: true,
          separator: ',',
          decimal: '.',
          decimalPlaces: 2
        }
      }
    }
  },
  computed: {
    today () {
      return moment().format('MMM, YYYY')
    },
    totalOpenBalance: {
      get: function () {
        return this.totalToday
      },
      set: function (data) {
        this.totalToday = data
      }
    },
    fundsColumnsData: {
      get: function () {
        return this.$store.getters.fundsColumnsData
      },
      set: function (data) {
        this.$store.dispatch('setFundsColumnsData', data)
      }
    },
    fundsRowsData: {
      get: function () {
        return this.$store.getters.fundsRowsData
      },
      set: function (data) {
        this.$store.dispatch('setFundsRowsData', data)
      }
    },
    kpiEarnedColumnsData: {
      get: function () {
        return this.$store.getters.kpiEarnedColumnsData
      },
      set: function (data) {
        this.$store.dispatch('setKpiEarnedColumnsData', data)
      }
    },
    kpiEarnedRowsData: {
      get: function () {
        return this.$store.getters.kpiEarnedRowsData
      },
      set: function (data) {
        this.$store.dispatch('setKpiEarnedRowsData', data)
      }
    }
  },
  methods: {
    getCurrentProjects (total) {
      if (!total.hover) return
      FundsApi.getCurrentProjects()
        .then((resp) => {
          const currentProjects = resp.data.currentProjects || []
          const managers = this.setServicesPM(currentProjects, 'earned', false, 2, true)
          const consultants = this.setServicesPM(currentProjects, 'earned', false, 1, true)
          const sortByDate = _.orderBy([...managers, ...consultants], ['paymentDate'], ['desc'])
          this.kpiEarnedRowsData = sortByDate
          this.openEarned = this.kpiEarnedRowsData.length > 0
          this.addActionsInfo()
        })
        .catch((err) => {
          console.error(err)
        })
    },
    saveModal (item, type) {
      if (type === 1) {
        this.createTransfer(item)
      } else if (type === 2) {
        this.editFund(item)
      } else if (type === 3) {
        this.createFund(item)
      }
      this.hideModal()
    },
    hideModal () {
      this.openTransferList = false
      this.openModal = false
      this.editableFund = false
      this.transferFund = false
      this.newFund = false
      this.openModalBalance = false
    },
    addActions () {
      _.forEach(this.fundsColumnsData, item => {
        if (item.actions) {
          item.actions.delete = this.deleteFund
          item.actions.setPosition = this.setPosition
          item.actions.edit = (item) => {
            this.modalTitle = 'Transfer'
            this.openModal = true
            this.transferFund = item
          }
          item.actions.settings = (item) => {
            this.modalTitle = 'Settings'
            this.openModal = true
            this.editableFund = item
          }
        }
      })
    },
    addActionsInfo () {
      _.forEach(this.optionsEarnedTable, item => {
        if (item.actionType === 'openProforma') {
          item.action = this.openProforma
        }
      })
    },
    openProforma (row) {
      const id = _.get(row, 'invoiceId', false)
      if (!id) return
      this.loaderRun = true
      InvoicesApi.getInvoiceById(id)
        .then(resp => {
          this.editableInvoice = {
            invoice: resp.data
          }
          this.proformaOpen = true
          this.loaderRun = false
        })
        .catch(err => {
          console.error(err)
          this.loaderRun = false
        })
    },
    closeProforma () {
      this.proformaOpen = false
    },
    setPosition (item) {
      FundsApi.setPositionFund(item)
    },
    openCreateFund () {
      this.modalTitle = 'Add new'
      this.openModal = true
      this.newFund = true
    },
    addEditAccess () {
      this.fundsRowsData.forEach(item => {
        item.draggable = true
        if (item.isVat) {
          item.noButton = {
            edit: true,
            delete: true
          }
        }
      })
    },
    getFunds ({limit, page}) {
      this.loaderRun = true
      FundsApi.getFunds({limit, page})
        .then((resp) => {
          this.fullFundsInfo = resp.data
          this.fundsRowsData = _.sortBy(resp.data.funds, 'listLocation')
          this.addEditAccess()
          this.getTotalFunds()
          this.loaderRun = false
        })
        .catch((err) => {
          this.loaderRun = false
          console.error(err)
        })
    },
    setOpenBalance () {
      if (this.canYouSetTotal === 'youCanSet') {
        this.openModal = true
        this.openModalBalance = true
      }
    },
    getTotalFunds () {
      FundsApi.getTotalFunds()
        .then((resp) => {
          const total = resp.data.totalCurrentBalance + resp.data.totalLastBalance
          const remain = total - resp.data.totalPaid
          this.totals.currentMonth.value = resp.data.totalCurrentBalance
          this.totals.paid.value = resp.data.totalPaid
          this.totals.remain.value = remain
        })
        .catch((err) => {
          console.error(err)
        })
    },
    getKpiBalance () {
      KpiApi.getBalance()
        .then((resp) => {
          if (resp.data.status !== 'isActive') {
            this.canYouSetTotal = 'youCanSet'
          }
          this.totalOpenBalance = resp.data.openBalance
          this.canYouSetTotal = false
        })
        .catch((err) => {
          if (err.response.status === 404) {
            this.canYouSetTotal = 'youCanSet'
          } else {
            console.error(err)
          }
        })
    },
    openBalance () {
      const number = this.validateNum(this.startOpenBalance)
      KpiApi.openBalance(number)
        .then((resp) => {
          this.totalOpenBalance = resp.data.openBalance
          this.getKpiBalance()
          this.getTotalFunds()
          this.getFunds({limit: this.defLimit})
          this.hideModal()
        })
        .catch((err) => {
          console.error(err)
        })
    },
    createFund (item) {
      FundsApi.createFund(item)
        .then((resp) => {
          this.fundsRowsData.push(resp.data)
          this.getTotalFunds()
        })
        .catch((err) => {
          console.error(err)
        })
    },
    editFund (item) {
      const index = _.findIndex(this.fundsRowsData, {id: item.id})
      FundsApi.editFund(item)
        .then((resp) => {
          this.$set(this.fundsRowsData, index, resp.data)
          this.addEditAccess()
          this.getTotalFunds()
        })
        .catch((err) => {
          console.error(err)
        })
    },
    deleteFund (id) {
      const index = _.findIndex(this.fundsRowsData, {id: id})
      FundsApi.deleteFund(id)
        .then((resp) => {
          this.fundsRowsData.splice(index, 1)
          this.getTotalFunds()
        })
        .catch((err) => {
          console.error(err)
        })
    },
    createTransfer (item) {
      FundsTransferApi.createTransfer(item)
        .then((resp) => {
          this.getFunds({limit: this.defLimit})
        })
        .catch((err) => {
          console.error(err)
        })
    },
    setKpiColumnsData () {
      const NewKpiEarnedColumnsData = []
      kpiEarnedTableColumns.forEach(item => {
        if (item.mark && item.mark !== 'earned') {
          return
        }
        NewKpiEarnedColumnsData.push(item)
      })
      this.kpiEarnedColumnsData = NewKpiEarnedColumnsData
    }
  },
  beforeMount () {
    setTimeout(() => {
      this.totalReady = true
    }, this.countUp.options.decimalPlaces * 1000)
    this.fundsColumnsData = fundsTableColumns
    this.getKpiBalance()
    this.getFunds({limit: this.defLimit})
    this.addActions()
    this.setKpiColumnsData()
  }
}
</script>

<style lang="scss" scoped>
  @import "../../../sass/variables";
  @import "../../../sass/mixins";

  .bb-funds {
    @include global-font;

    &-header {
      display: flex;
      align-items: center;
      margin: 14px auto;

      &-info {
        position: relative;
        z-index: 1;
        display: flex;

        &__wrapper {
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }

        &__title {
          color: rgba($color-dusty-gray, 0.7);
          font-size: 13px;
          line-height: 16px;
        }

        &__item {
          min-width: auto;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          color: $color-accent;
          font-size: 16px;
          line-height: 19px;
          font-weight: 600;
          background: $color-white;
          border-radius: 8px;
          padding: 12px 14px;
          margin-right: 15px;
          max-height: 42px;
          margin-top: 6px;

          &_active {
            cursor: pointer;

            &:hover {
              color: $color-accent-hover;
            }
          }
        }

        &__total {
          cursor: pointer;

          &:hover {
            box-shadow: 0 0 15px rgba($color-black, 0.1);
          }
        }

        &__icon {
          margin-right: 13px;

          &_calendar {
            font-size: 18px;
          }

          &_coin {
            font-size: 26px;
          }

          &:before {
            margin: 0;
          }
        }
      }

      &-buttons {
        display: flex;
        margin-left: auto;

        &__item {
          padding: 0 20px;
          height: 42px;
          margin-left: 14px;
        }
      }
    }

    &-modal {
      max-width: 70vw;
      padding: 0 8px;

      &__title {
        text-align: center;
        font-weight: 600;
        font-size: 18px;
        line-height: 21px;
        margin-bottom: 25px;
      }

      &__btn {
        width: 117px;
        margin: 20px auto auto;
      }

      &__button {
        text-transform: uppercase;
        width: 100%;
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/Finance/Funds/Funds.vue